import { GetContactsWeightedScoresQueryVariables } from '@zaplify/graphql';
import React, { createContext, useContext, useEffect, useState } from 'react';

type GqlQueryVariablesContextType = {
    gqlQueryVariables: GetContactsWeightedScoresQueryVariables;
    setGqlQueryVariables: (gqlQueryVariables: GetContactsWeightedScoresQueryVariables) => void;
    filterContactIds: string[] | undefined;
    setFilterContactIds: (filterContactIds: string[] | undefined) => void;
    filterGroupIds: string[] | undefined;
    setFilterGroupIds: (filterGroupIds: string[] | undefined) => void;
    activeFilterId: string | undefined;
    setActiveFilterId: (activeFilterId: string | undefined) => void;
    userRequest: string | undefined;
    setUserRequest: (userRequest: string | undefined) => void;
    error: string | undefined;
    setError: (error: string | undefined) => void;
    textSearchQuery: string | undefined;
    setTextSearchQuery: (textSearchQuery: string | undefined) => void;
};

const GqlQueryVariablesContext = createContext<GqlQueryVariablesContextType | undefined>(undefined);

export function GqlQueryVariablesProvider({ children }: { children: React.ReactNode }) {
    const [gqlQueryVariables, setGqlQueryVariables] = useState<GetContactsWeightedScoresQueryVariables | undefined>(
        undefined
    );
    const [filterContactIds, setFilterContactIds] = useState<string[] | undefined>(undefined);
    const [filterGroupIds, setFilterGroupIds] = useState<string[] | undefined>(undefined);
    const [activeFilterId, setActiveFilterId] = useState<string | undefined>(undefined);
    const [textSearchQuery, setTextSearchQuery] = useState<string | undefined>(undefined);

    const [userRequest, setUserRequest] = useState<string | undefined>(undefined);

    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!activeFilterId) {
            setGqlQueryVariables(undefined);
        }
    }, [activeFilterId]);

    useEffect(() => {
        if (!filterContactIds?.[0]) {
            setGqlQueryVariables(undefined);
            setActiveFilterId(undefined);
            setUserRequest(undefined);
            setError(undefined);
        }
    }, [filterContactIds]);

    useEffect(() => {
        if (textSearchQuery) {
            setGqlQueryVariables(undefined);
            setFilterContactIds(undefined);
            setActiveFilterId(undefined);
            setUserRequest(undefined);
            setError(undefined);
        }
    }, [textSearchQuery]);

    useEffect(() => {
        if (filterGroupIds?.[0]) {
            setGqlQueryVariables(undefined);
            setFilterContactIds(undefined);
            setActiveFilterId(undefined);
            setUserRequest(undefined);
            setError(undefined);
            setTextSearchQuery(undefined);
        }
    }, [filterGroupIds]);

    return (
        <GqlQueryVariablesContext.Provider
            value={{
                gqlQueryVariables,
                setGqlQueryVariables,
                filterContactIds,
                setFilterContactIds,
                filterGroupIds,
                setFilterGroupIds,
                activeFilterId,
                setActiveFilterId,
                userRequest,
                setUserRequest,
                error,
                setError,
                textSearchQuery,
                setTextSearchQuery,
            }}
        >
            {children}
        </GqlQueryVariablesContext.Provider>
    );
}

export function useGqlQueryVariables() {
    const context = useContext(GqlQueryVariablesContext);
    if (context === undefined) {
        throw new Error('useGqlQueryVariables must be used within a GqlQueryVariablesProvider');
    }
    return context;
}
